import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Button, Spinner, StepperModal } from '@ifs-gcp/components';
import {
  type AuditCreation,
  type IAppContextProvider,
  type IAudit,
  type ITemplateManager,
  type ITemplateNode,
  QuestionValidator,
  type Standards,
} from '@ifs-gcp/core';
import { isQuestionNode } from '@ifs-gcp/core/helpers';
import type { StartProcessTemplateRoot, StepContentChild } from '@ifs-gcp/core/models';

import { ROUTE_AUDIT_CASE } from '@/constants/router';
import { useErrorHandling } from '@/hooks/useErrorHandling';
import { useCreateAuditCaseForNewAudit } from '@/queries/create-audit-case-new-audit';
import { useUserStore } from '@/store/userStore';
import { createAuditFileBtoa } from '@/utils/createAuditFile';
import { getTranslation } from '@/utils/translations';

import { TemplateElement } from './dashboard-template-element';

export type StepStatus = 'incomplete' | 'loading' | 'complete';

const getStepValidity = (step: ITemplateNode<StepContentChild>, domain: AuditCreation) => {
  if (!step.children) {
    return true;
  }

  return step.children.every((element) => {
    if (!('requiredCondition' in element.props)) {
      return true;
    }

    if (isQuestionNode(element)) {
      const answer = domain.modulesHandler.modules
        .get(element.props.context[0])
        ?.questions.questions.get(element.props.id)?.answer;

      const questionValidator = new QuestionValidator(element, domain.conditionHandler);

      const questionError = questionValidator.validate(answer);

      return questionError === null;
    }

    return domain.conditionHandler.validate(element.props.requiredCondition);
  });
};

type DashboardModalProps = {
  domain: AuditCreation;
  supplierId: string;
  standards: Standards;
  userProvider: IAppContextProvider;
  startProcessTemplateHandler: ITemplateManager<StartProcessTemplateRoot['templateCode']>;
  handleCloseModal: () => void;
};

export type StepCompleteData = {
  status: 'success' | 'error' | null;
  details?: Record<string, unknown>;
};

export const DashboardModal = observer(
  ({
    domain,
    supplierId,
    standards,
    userProvider,
    handleCloseModal,
    startProcessTemplateHandler,
  }: DashboardModalProps) => {
    const {
      t,
      i18n: { language },
    } = useTranslation();

    const navigate = useNavigate();
    const { mutateAsync: createAuditCase, isPending: isAuditCreatePending } =
      useCreateAuditCaseForNewAudit();
    const handleErrors = useErrorHandling();
    const moduleIdsInUse = useUserStore((state) => state.moduleIdsInUse);

    const [completedActions, setCompletedActions] = useState<{
      [key: string]: Omit<StepCompleteData, 'type'>;
    }>({});

    const steps = startProcessTemplateHandler.schema.children.map<{
      status: StepStatus;
      template: ITemplateNode<StepContentChild>;
    }>((step, _, array) => {
      let status: StepStatus = 'incomplete';

      const isLastStep = step.props.value === array.at(-1)?.props.value;

      if (isLastStep) {
        if (completedActions[step.props.value]?.status === 'success') {
          status = 'complete';
        } else if (completedActions[step.props.value]?.status !== 'error') {
          status = 'loading';
        }

        return {
          status,
          template: step,
        };
      }

      const isValid = getStepValidity(step, domain);

      if (isValid) {
        status = 'complete';
      }

      return {
        status,
        template: step,
      };
    });

    const availableSteps = useMemo(
      () => steps.filter((step) => !step.template.props.disabledCondition),
      [steps],
    );

    const [activeStepIndex, setActiveStepIndex] = useState(() => {
      return steps.findIndex(
        (step) => step.template.props.value === availableSteps[0].template.props.value,
      );
    });

    const availableModules = useMemo(() => {
      const modulesIds = availableSteps.flatMap((step) =>
        step.template.children.flatMap((child) =>
          'context' in child.props ? (child.props.context ?? []) : [],
        ),
      );
      return Array.from(new Set(modulesIds));
    }, [availableSteps]);

    const showDisableWarning = useMemo(() => {
      if (!moduleIdsInUse || moduleIdsInUse.length === 0) {
        return false;
      }

      const moduleIdsInUseSet = new Set(moduleIdsInUse);

      return steps[activeStepIndex]?.template.children.some(
        (child) =>
          isQuestionNode(child) &&
          child.props.context.some((questionContext) => moduleIdsInUseSet.has(questionContext)),
      );
    }, [availableModules, moduleIdsInUse, steps, activeStepIndex]);

    const stepperSteps = useMemo(
      () =>
        steps.map((step) => ({
          id: step.template.props.value,
          title: step.template.props.title[language],
          status: step.status,
        })),
      [steps, language],
    );

    const hasPreviousStep =
      activeStepIndex > 0 && !steps[activeStepIndex - 1].template.props.disabledCondition;
    const hasNextStep = activeStepIndex < steps.length - 1;

    const handleBack = (stepTemplate: ITemplateNode<StepContentChild>) => {
      if (!hasPreviousStep) {
        throw new Error('Cannot go back as start process does not have a previous step');
      }

      if (!hasNextStep) {
        setCompletedActions((prev) => ({
          ...prev,
          [stepTemplate.props.value]: { status: null, details: undefined },
        }));
      }

      setActiveStepIndex(activeStepIndex - 1);
    };

    const handleNext = () => {
      if (!hasNextStep) {
        throw new Error('Cannot continue as start process does not have any more steps');
      }

      setActiveStepIndex(activeStepIndex + 1);
    };

    const handleStart = async () => {
      const audit = completedActions[steps[activeStepIndex].template.props.value]?.details
        ?.audit as IAudit;

      try {
        const auditFileBtoa = createAuditFileBtoa(audit);

        const moduleIds = Array.from(audit.modules.modules.keys());

        const { auditCaseUuid } = await createAuditCase({
          supplierId,
          moduleIds,
          inputFile: `data:application/vnd.ifs.axpx-neo;base64,${auditFileBtoa}`,
        });

        navigate(ROUTE_AUDIT_CASE(auditCaseUuid));
      } catch (error) {
        handleErrors(error);
      }
    };

    const handleElementComplete = (event: StepCompleteData) => {
      setCompletedActions((prev) => ({
        ...prev,
        [steps[activeStepIndex].template.props.value]: {
          status: event.status,
          details: event.details,
        },
      }));
    };

    useEffect(() => {
      /**
       * Cleanup modules handler when component unmounts
       */
      return () => {
        domain.modulesHandler.dispose();
      };
    }, [domain.modulesHandler]);

    const nextButtonContent = useMemo(() => {
      if (isAuditCreatePending) {
        return <Spinner size={20} />;
      }

      if (hasNextStep) {
        return t('actions.next');
      }

      return t('actions.start');
    }, [hasNextStep, isAuditCreatePending, t]);

    return (
      <StepperModal
        modalTitle={t('dashboard.start_modal.title')}
        stepper={{
          steps: stepperSteps,
          activeStepTitle: getTranslation(steps[activeStepIndex].template.props.title, language),
          activeStepIndex,
        }}
        buttons={{
          backButton: () =>
            hasPreviousStep ? (
              <Button
                onClick={() => handleBack(steps[activeStepIndex].template)}
                disabled={steps[activeStepIndex].status !== 'complete'}
              >
                {t('actions.back')}
              </Button>
            ) : null,
          nextButton: () => (
            <Button
              onClick={hasNextStep ? handleNext : handleStart}
              variant="contained"
              color={hasNextStep ? 'secondary' : 'primary'}
              disabled={steps[activeStepIndex].status !== 'complete' || isAuditCreatePending}
            >
              {nextButtonContent}
            </Button>
          ),
        }}
        onClose={handleCloseModal}
      >
        {steps[activeStepIndex].template.children.map((nextElement, index) => (
          <TemplateElement
            key={`${nextElement.type}-${index}`}
            moduleIdsInUse={moduleIdsInUse ?? []}
            element={nextElement}
            domain={domain}
            status={steps[activeStepIndex].status}
            standards={standards}
            userProvider={userProvider}
            showDisableWarning={showDisableWarning}
            onComplete={handleElementComplete}
          />
        ))}
      </StepperModal>
    );
  },
);
